import type { CopyJobEntry } from 'atoms/Rtool/copyJobAtoms';
import { buildCalcLabel } from 'atoms/recentCalcsAtom';
import type { CopyJobSnapshot } from 'api/copyJobs';

// コピー進捗の表示文言（issue #44）。ETA・「現在処理中のtask」は出さない
// （前者はissue本文の方針、後者はBE契約訂正でtasks[].statusにrunningが来ず判別不能なため）。
// progress.total_tasks（HEV=11/BEV=2）はハードコードせず、常にsnapshotの値を使う。
export const buildCopyJobProgressCaption = (entry: CopyJobEntry): string => {
    if (entry.watchState === 'error') {
        return '進捗の取得に失敗しました。画面を再読み込みしてください。';
    }
    const { snapshot } = entry;
    if (!snapshot) {
        return 'コピーを受け付けました。状況を取得しています…';
    }
    if (snapshot.started_at === null) {
        return 'コピーを受け付けました。処理を開始する準備をしています。';
    }
    const { done_tasks, total_tasks } = snapshot.progress;
    return `${done_tasks}/${total_tasks} タスク完了`;
};

// ヘッダーの一覧表示用ラベル（powertrain + 計算セットのパス）。
export const buildCopyJobDestinationLabel = (entry: CopyJobEntry): string =>
    `${entry.powertrain.toUpperCase()}: ${buildCalcLabel(entry.config)}`;

// incomplete_tasksの値はtask名そのもの（issue #41 §3.7）。HEVは"gear:3rd"形式でgear:プレフィックス
// が付き、BEVは"conditions"/"common"でプレフィックスが付かない。表示時のみgear:を落とす
// （パース・値の受け取り自体はプレフィックス付き前提のまま。issue #41 §6の落とし穴5）。
export const formatIncompleteTaskLabel = (task: string): string =>
    task.startsWith('gear:') ? task.slice('gear:'.length) : task;

// failed（ジョブ自体の中断）用の文言。issue #45 §3の要件どおりmessageは参照せず、
// failed_reason（サーバ側の中断理由。ユーザー向けには区別しない）ではなくincomplete_tasksと
// summary.succeededから「どこまで進み・再実行すれば補われる」旨を組む。
// summary合計と全件数の検算はしない（failedのsummaryは完走task分のみで全件数に届かないため。
// issue #41 §3.2/§6の落とし穴6）。
export const buildFailedJobMessage = (snapshot: CopyJobSnapshot): string => {
    const incompleteTasks = (snapshot.incomplete_tasks ?? []).map(formatIncompleteTaskLabel);

    const incompleteSuffix = incompleteTasks.length > 0 ? `（未完了: ${incompleteTasks.join(', ')}）` : '';

    // succeeded=0のケース（enqueue_failed等で1つもtaskが完走しない場合に加え、done_tasks>0でも
    // 完走task内が全skip/failedだった場合を含む。/code-review xhigh所見: done_tasks===0だけでは
    // 後者を拾えず「0件までコピー済みです」という体裁の紛らわしい表示になっていた）は
    // 「◯件までコピー済み」という言い回し自体が成立しないため専用文言にするが、未完了task列挙・
    // 再実行案内は成功1件以上のケースと揃える（issue #45回帰）。
    if (snapshot.summary.succeeded === 0) {
        return `コピー処理が中断され、1件も登録されませんでした${incompleteSuffix}。もう一度コピーを実行すると未コピー分が補われます。`;
    }

    return `コピー処理が中断されました。${snapshot.summary.succeeded}件までコピー済みです${incompleteSuffix}。もう一度コピーを実行すると未コピー分が補われます。`;
};
